import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
  getDocs,
  increment,
} from 'firebase/firestore';
import { db } from './firebaseClient';
import {
  findOrCreatePantryItem,
  incrementPantryQuantity,
  getPantryItem,
} from './pantryService';

const COLLECTION_NAME = 'shopping_list_items';

/**
 * Add a new shopping list item
 */
export const addShoppingListItem = async (itemData) => {
  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...itemData,
    quantity: itemData.quantity || 1,
    isPurchased: false,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: docRef.id, ...itemData };
};

/**
 * Update an existing shopping list item
 */
export const updateShoppingListItem = async (itemId, updateData) => {
  const itemRef = doc(db, COLLECTION_NAME, itemId);
  await updateDoc(itemRef, {
    ...updateData,
    updatedAt: serverTimestamp(),
  });
  return { id: itemId, ...updateData };
};

/**
 * Delete a shopping list item
 */
export const deleteShoppingListItem = async (itemId) => {
  await deleteDoc(doc(db, COLLECTION_NAME, itemId));
  return { success: true, id: itemId };
};

/**
 * Add shopping list item to pantry without marking as purchased
 */
export const addShoppingListItemToPantry = async (itemId) => {
  const itemRef = doc(db, COLLECTION_NAME, itemId);
  
  // Get the current item data
  const itemDoc = await getDoc(itemRef);
  if (!itemDoc.exists()) {
    throw new Error('Shopping list item not found');
  }
  
  const itemData = itemDoc.data();
  
  // Add to pantry or increment quantity
  if (itemData.pantryItemId) {
    // If linked to a pantry item, increment its quantity
    await incrementPantryQuantity(itemData.pantryItemId, itemData.quantity || 1);
  } else {
    // Create or find pantry item
    const pantryItem = await findOrCreatePantryItem(itemData.name, {
      quantity: itemData.quantity || 1,
      unit: itemData.unit || '',
      category: itemData.category || '',
      price: itemData.price || 0,
    });
    
    // Update shopping list item with pantry item ID
    await updateDoc(itemRef, {
      pantryItemId: pantryItem.id,
    });
  }
  
  return { success: true, id: itemId };
};

/**
 * Mark shopping list item as purchased
 * Automatically adds/increments the item in the pantry
 */
export const markShoppingListItemAsPurchased = async (itemId) => {
  const batch = writeBatch(db);
  const itemRef = doc(db, COLLECTION_NAME, itemId);
  
  // Get the current item data
  const itemDoc = await getDoc(itemRef);
  if (!itemDoc.exists()) {
    throw new Error('Shopping list item not found');
  }
  
  const itemData = itemDoc.data();
  
  // Mark as purchased
  batch.update(itemRef, {
    isPurchased: true,
    purchasedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  
  // Add to pantry or increment quantity
  if (itemData.pantryItemId) {
    // If linked to a pantry item, increment its quantity
    const pantryItemRef = doc(db, 'pantry_items', itemData.pantryItemId);
    batch.update(pantryItemRef, {
      quantity: increment(itemData.quantity || 1),
      updatedAt: serverTimestamp(),
    });
  }
  
  await batch.commit();
  
  // Handle pantry item creation if not linked
  if (!itemData.pantryItemId) {
    const pantryItem = await findOrCreatePantryItem(itemData.name, {
      quantity: itemData.quantity || 1,
      unit: itemData.unit || '',
      category: itemData.category || '',
      price: itemData.price || 0,
    });
    
    // Update shopping list item with pantry item ID
    await updateDoc(itemRef, {
      pantryItemId: pantryItem.id,
    });
  }
  
  return { success: true, id: itemId };
};

/**
 * Batch add items to shopping list from pantry
 */
export const addPantryItemsToShoppingList = async (pantryItemIds) => {
  const batch = writeBatch(db);
  const items = [];
  
  // Fetch all pantry items
  const pantryItems = await Promise.all(
    pantryItemIds.map(id => getPantryItem(id))
  );
  
  // Add each to shopping list
  for (const pantryItem of pantryItems) {
    if (!pantryItem) continue;
    
    const shoppingListItemRef = doc(collection(db, COLLECTION_NAME));
    batch.set(shoppingListItemRef, {
      name: pantryItem.name,
      quantity: pantryItem.quantity || 1,
      unit: pantryItem.unit || '',
      category: pantryItem.category || '',
      price: pantryItem.price || 0,
      pantryItemId: pantryItem.id,
      isPurchased: false,
      addedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    items.push({
      id: shoppingListItemRef.id,
      name: pantryItem.name,
      quantity: pantryItem.quantity || 1,
      unit: pantryItem.unit || '',
      category: pantryItem.category || '',
      price: pantryItem.price || 0,
      pantryItemId: pantryItem.id,
    });
  }
  
  await batch.commit();
  return items;
};

/**
 * Clear all items from shopping list (for resetting for next shopping trip)
 */
export const clearAllShoppingListItems = async () => {
  const batch = writeBatch(db);
  const snapshot = await getDocs(collection(db, COLLECTION_NAME));
  
  // Delete all items (including purchased ones)
  snapshot.docs.forEach((docSnapshot) => {
    batch.delete(docSnapshot.ref);
  });
  
  await batch.commit();
  return { success: true, deletedCount: snapshot.docs.length };
};

/**
 * Complete shopping - mark all unpurchased items as purchased and then clear the list
 * This ensures all items are added to pantry with correct quantities
 */
export const completeShopping = async (items) => {
  const unpurchasedItems = items.filter(item => !item.isPurchased);
  const errors = [];
  const successfulItems = [];
  
  // First, ensure all unpurchased items are added to pantry
  // This will mark them as purchased AND add/increment them in pantry
  for (const item of unpurchasedItems) {
    try {
      await markShoppingListItemAsPurchased(item.id);
      successfulItems.push(item.name);
    } catch (error) {
      console.error(`Error processing item ${item.id} (${item.name}):`, error);
      errors.push({ name: item.name, error: error.message });
      // Continue with other items even if one fails
    }
  }
  
  // If there were errors, log them but don't fail completely
  if (errors.length > 0) {
    console.warn('Some items failed to process:', errors);
  }
  
  // Then clear all items from shopping list (including purchased ones)
  try {
    await clearAllShoppingListItems();
  } catch (error) {
    console.error('Error clearing shopping list:', error);
    throw new Error(`Failed to clear shopping list: ${error.message}`);
  }
  
  return {
    success: true,
    itemsProcessed: successfulItems.length,
    itemsFailed: errors.length,
    successfulItems,
    failedItems: errors,
    itemsCleared: items.length,
  };
};

